import axios from 'axios';

/**
 * What the change-password screen needs, carried in router state (in memory
 * only, never persisted: there is no session yet and the temporary password is
 * a credential).
 */
export interface PasswordResetHandoff {
  email: string;
  currentPassword: string;
  tenantId: string;
}

/**
 * Recognises the login answer for an account that holds a TEMPORARY password:
 * 403 `{ error: "password_reset_required", tenantId }` and NO token. The
 * credentials were valid; the server refuses to issue a session until the
 * password is changed. That is not a lock and not a suspension, so the caller
 * routes to the change-password screen instead of showing an error.
 *
 * Returns the hand-off for the change-password screen, or null for any other
 * failure. `tenantId` is the server-RESOLVED canonical id from the 403 body
 * (OP-221): what the user typed may be a slug the gateway tenant gate cannot
 * resolve for the follow-up call. Falls back to the typed value only when the
 * body carries none. Mirrors admin-portal `useAuth.login`.
 */
export function passwordResetHandoff(
  error: unknown,
  typed: { email: string; password: string; tenantId: string }
): PasswordResetHandoff | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 403) return null;
  const body = error.response.data as { error?: string; tenantId?: string } | undefined;
  if (body?.error !== 'password_reset_required') return null;
  return {
    email: typed.email,
    currentPassword: typed.password,
    tenantId: body.tenantId || typed.tenantId,
  };
}
